import type { TOC } from '@ember/component/template-only'
import type { ManageClassesModel } from 'teacher-ui-client/manage/classes/route'
import type ManageClassesController from 'teacher-ui-client/manage/classes/controller'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import { t } from 'ember-intl'
import { RolloverHeaderReturnLink } from 'teacher-ui-client/components/rollover/header-return-link'
import { PageHeaderManageTabs } from 'teacher-ui-client/components/page-header/manage-tabs'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { usesExternalRostering } from 'teacher-ui-client/helpers/uses-external-rostering'
import { TilesManageActions } from 'teacher-ui-client/components/tiles/manage-actions'
import { CreateSchoolClass } from 'teacher-ui-client/components/manage/classes/create-school-class'
import { HRText } from 'teacher-ui-client/components/base/hr-text'
import { JoinSchoolClass } from 'teacher-ui-client/components/manage/classes/join-school-class'
import { DataOperationsTable } from 'teacher-ui-client/components/data-operations/table'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { FormComponentsFormSelect } from 'teacher-ui-client/components/form-components/form-select'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { featureFlag } from 'teacher-ui-client/helpers/feature-flag'
import { uniqueId, fn, array } from '@ember/helper'
import { on } from '@ember/modifier'
import { pick } from 'teacher-ui-client/helpers/pick'
import { not, eq } from 'ember-truth-helpers'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { includes } from 'teacher-ui-client/helpers/includes'
import { UiButtonLinkTo } from 'teacher-ui-client/components/ui-button/link-to'
import joinList from 'teacher-ui-client/helpers/join-list'
import { LayoutBlocksModal } from 'teacher-ui-client/components/layout-blocks/modal'
import { LeaveClassConfirmationForm } from 'teacher-ui-client/components/form-wrapper/leave-class-confirmation-form'
import { DeleteClassConfirmationForm } from 'teacher-ui-client/components/form-wrapper/delete-class-confirmation-form'
import { FormDeleteClasses } from 'teacher-ui-client/components/form/delete-classes'
import { SortType } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'
import { OneRosterNotice } from 'teacher-ui-client/components/manage/one-roster/notice'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import { ColumnSortIcon } from 'teacher-ui-client/components/data-operations/table/column-sort-icon'

const oneRosterUrl = join(config.oneRosterUrl, 'login').toString().concat('?return_to=/manage-classes')

interface Signature {
  Args: {
    model: ManageClassesModel
    controller: ManageClassesController
  }
}

interface OneRosterSignature {
  Args: {
    model: ManageClassesModel
  }
}

export class ManageClassesPage extends Component<Signature> {
  @service featureControl!: FeatureControlService

  get showOneRosterTemplate() {
    return this.featureControl.canAccess('one_roster')
  }

  <template>
    <PageHeader>
      <:title>
        <h1>
          {{t "manage.title.classes"}}
        </h1>
      </:title>
      <:lowerLeft>
        <RolloverHeaderReturnLink />
      </:lowerLeft>
      <:lowerCenter>
        <PageHeaderManageTabs class="m-3" @themeKey={{themeKey @model.subscriptionType}} />
      </:lowerCenter>
    </PageHeader>
    {{#if this.showOneRosterTemplate}}
      <OneRosterManageClasses @model={{@model}} />
    {{else}}<ManageClasses @model={{@model}} @controller={{@controller}} />{{/if}}
  </template>
}

const OneRosterManageClasses: TOC<OneRosterSignature> = <template>
  <OneRosterNotice @linkUrl={{oneRosterUrl}} @linkText={{t "manage.3essentials.marketingTile.goToClasses"}} />
</template>

const ManageClasses: TOC<Signature> = <template>
  {{#unless (usesExternalRostering)}}
    <TilesManageActions
      data-test-manage-actions-tile
      class="mb-6 p-4"
      @themeKey={{themeKey @model.subscriptionType}}
      @title={{t "manage.schoolClasses.createPanelHeading"}}
      as |tile|
    >
      <tile.formArea class="max-w-2xl">
        <h5>{{t "manage.schoolClasses.createFormHeading"}}</h5>
        <CreateSchoolClass
          data-test-create-classes
          @teachers={{@controller.teachersForCreateSchoolClass}}
          @showChooseTeacherControl={{@controller.isSubco}}
          @createSchoolClassAction={{@controller.createSchoolClass}}
        />
        {{#if @controller.showJoinSchoolClass}}
          <HRText @text="OR" />
          <h5 data-test-join-school-class-title>{{t "manage.schoolClasses.joinFormHeading"}}</h5>
          <JoinSchoolClass
            data-test-join-classes
            @teacher={{@controller.currentTeacher}}
            @teachers={{@controller.nonTrialTeachers}}
            @subscriptionType={{@model.subscriptionType}}
          />
        {{/if}}
      </tile.formArea>
    </TilesManageActions>
  {{/unless}}
  <DataOperationsTable @dataOperationsConfig={{@controller.dataOperationsConfig}} as |selectRecords sorting pagination|>
    <div class="border-dusty-black-100 overflow-visible rounded-md border">
      <div class="{{@controller.tableTheme.controlsBg}}">
        <div class="flex items-center p-5">
          {{#if @controller.showFilterByTeacher}}
            {{#let (uniqueId) as |uniqueId|}}
              <label for="teacher-filter-{{uniqueId}}" class="mr-2 font-normal">
                {{t "manage.manageClasses.filterByGroupLabel"}}
              </label>
              <FormComponentsFormSelect
                id="teacher-filter-{{uniqueId}}"
                @defaultText={{t "manage.schoolClasses.allTeachers"}}
                @value={{@controller.teacherFilter}}
                @optionClick={{@controller.setGroupFilter}}
                @optionTemplate={{@controller.groupFilterDropdownOptionTemplate}}
                @options={{@controller.teacherOptionsSorted}}
                @valueLookupKey="value"
                @resetAction={{@controller.resetGroupFilter}}
                @search={{true}}
                @searchPlaceholder={{t "reporting.dropdownSearchTeachers"}}
              />
            {{/let}}
          {{/if}}
          <InputText
            class="ml-auto"
            type="search"
            role="searchbox"
            name="search-filter"
            value={{@controller.textFilter}}
            aria-label={{t "manage.manageClasses.classes.filterClasses.label"}}
            placeholder={{t "manage.manageClasses.classes.filterClasses.placeholder"}}
            @themeKey={{themeKey @model.subscriptionType}}
            @icon="search"
            @iconEnd={{true}}
            {{on "input" (pick "target.value" @controller.setTextFilter)}}
          />
        </div>
        {{#if (featureFlag "tui_school_class_bulk_delete")}}
          <div class="p-5">
            <UiButton
              @appearance={{ButtonAppearance.muted}}
              @disabled={{not selectRecords.anySelected}}
              class="print:hidden"
              {{on "click" (fn @controller.showDeleteClassesModal true)}}
              aria-label={{t "manage.manageClasses.classes.actions.deleteSelected"}}
            >{{t "manage.manageClasses.classes.actions.deleteSelected"}}</UiButton>
          </div>
        {{/if}}
      </div>
      <table data-test-manage-classes-table class="table-set-height w-full border-t border-white">
        <thead class={{@controller.tableTheme.headerBg}}>
          <tr class="divide-x divide-white border-b border-white">
            {{#if (featureFlag "tui_school_class_bulk_delete")}}
              <th class="w-px">
                <label class="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-1 p-3">
                  <span>{{t "manage.selectAllLabel"}}</span>
                  <input
                    data-test-select-all-records
                    type="checkbox"
                    class="cursor-pointer"
                    checked={{selectRecords.allSelected}}
                    {{on "change" selectRecords.toggleAll}}
                  />
                </label>
              </th>
            {{/if}}
            <th>
              <button
                type="button"
                class="flex h-full w-full items-center gap-1 p-3 text-left whitespace-nowrap"
                {{on "click" (fn sorting.sort SortType.ByValue "name")}}
              >
                <span>{{t "manage.manageClasses.classes.name"}}</span>
                <ColumnSortIcon @columnName="name" @sortCol={{sorting.sortCol}} @sortDir={{sorting.sortDir}} />

              </button>
            </th>
            <th>
              <button
                type="button"
                class="flex h-full w-full items-center gap-1 p-3 text-left whitespace-nowrap"
                {{on "click" (fn sorting.sort SortType.ByNumber "students.length")}}
              >
                {{t "manage.manageClasses.classes.studentCount"}}
                <ColumnSortIcon
                  @columnName="students.length"
                  @sortCol={{sorting.sortCol}}
                  @sortDir={{sorting.sortDir}}
                />

              </button>
            </th>
            <th>
              <button
                type="button"
                class="flex h-full w-full items-center gap-1 p-3 text-left whitespace-nowrap"
                {{on "click" (fn sorting.sort SortType.ByValue "teacherNames")}}
              >
                {{t "manage.manageClasses.classes.teacherNames"}}
                <ColumnSortIcon @columnName="teacherNames" @sortCol={{sorting.sortCol}} @sortDir={{sorting.sortDir}} />

              </button>
            </th>
            <th class="p-3"></th>
          </tr>
        </thead>
        <tbody>
          {{#each pagination.currentPageRecords as |row index|}}
            {{#let (eq @controller.editingRecordId row.id) as |isEditing|}}
              <tr
                data-test-body-row={{index}}
                class={{if isEditing @controller.tableTheme.bodyRowBgEditing @controller.tableTheme.bodyRowBg}}
              >
                {{#if (featureFlag "tui_school_class_bulk_delete")}}
                  <td>
                    <label class="flex h-full w-full cursor-pointer flex-col items-center justify-center p-3">
                      <span class="hidden">{{t "manage.manageClasses.selectSingleRecordLabel" name=row.name}}</span>
                      <input
                        data-test-select-record
                        type="checkbox"
                        class="cursor-pointer"
                        checked={{includes selectRecords.selectedIds row.id}}
                        {{on "change" (fn selectRecords.toggle row.id)}}
                      />
                    </label>
                  </td>
                {{/if}}
                <td class="p-3 {{if isEditing 'font-bold'}}" data-test-manage-class-row-name>
                  {{row.name}}
                </td>
                <td class="p-3 {{if isEditing 'font-bold'}}">
                  {{row.studentCount}}
                </td>
                <td class="p-3 {{if isEditing 'font-bold'}}" data-test-teacher-names>
                  {{joinList row.teacherNames ", "}}
                </td>
                <td class="flex flex-wrap gap-1 p-3 whitespace-nowrap">
                  {{#unless (usesExternalRostering)}}
                    <UiButton
                      data-test-leave-class
                      disabled={{not (includes row.teachers @controller.currentTeacher)}}
                      @appearance={{ButtonAppearance.muted}}
                      {{on "click" (fn @controller.leaveClass row.id)}}
                    >
                      <PhosphorIcon @icon="phosphor-sign-out-bold" />
                      {{t "manage.manageClasses.classes.actions.leaveClass"}}
                    </UiButton>
                    {{#unless (featureFlag "tui_school_class_bulk_delete")}}
                      {{! when removing tui_school_class_bulk_delete, remove the individual class Delete buttons for consistency across other manage pages }}
                      <UiButton
                        data-test-delete-class
                        @appearance={{ButtonAppearance.muted}}
                        {{on "click" (fn @controller.deleteClass row.id)}}
                      >
                        <PhosphorIcon @icon="phosphor-trash-fill" class="h-5 w-5" />
                        {{t "manage.manageClasses.classes.actions.delete"}}
                      </UiButton>
                    {{/unless}}
                  {{/unless}}
                  {{#if isEditing}}
                    <UiButtonLinkTo
                      data-test-edit-class-close
                      @route="manage.classes"
                      @appearance={{ButtonAppearance.muted}}
                    >
                      <PhosphorIcon @icon="phosphor-x-bold" />
                      {{t "manage.manageClasses.classes.actions.close"}}
                    </UiButtonLinkTo>
                  {{else}}
                    <UiButtonLinkTo
                      data-test-edit-class-open
                      @route="manage.classes.school-class"
                      @models={{array row.id}}
                      @appearance={{ButtonAppearance.muted}}
                    >
                      <PhosphorIcon @icon="phosphor-note-pencil-fill" />
                      {{t "manage.manageClasses.classes.actions.edit"}}
                    </UiButtonLinkTo>
                  {{/if}}
                </td>
              </tr>
              {{#if isEditing}}
                <tr>
                  <td
                    colspan="{{if (featureFlag 'tui_school_class_bulk_delete') '5' '4'}}"
                    class={{if isEditing "bg-juicy-orange-50 p-3"}}
                  >
                    {{outlet}}
                  </td>
                </tr>
              {{/if}}
            {{/let}}
          {{else}}
            <tr>
              <td colspan="4" class="p-3 text-center italic">
                {{t "manage.noDataAvailable"}}
              </td>
            </tr>
          {{/each}}
        </tbody>
      </table>
    </div>
    {{#if @controller.schoolClassActingUpon}}
      <LayoutBlocksModal
        @title={{t "manage.manageClasses.classes.leaveClass.title"}}
        @showModal={{@controller.leaveClassModalOpen}}
        @close={{fn @controller.showLeaveClassModal false}}
      >
        <LeaveClassConfirmationForm
          @schoolClass={{@controller.schoolClassActingUpon}}
          {{! partial application of submitLeaveClassModal action }}
          @onSubmit={{fn @controller.submitLeaveClassModal pagination}}
          @onClose={{fn @controller.showLeaveClassModal false}}
        />
      </LayoutBlocksModal>
    {{/if}}
    <LayoutBlocksModal
      @title={{t "manage.manageClasses.classes.deleteClass.title"}}
      @showModal={{@controller.deleteClassModalOpen}}
      @close={{fn @controller.showDeleteClassModal false}}
    >
      <DeleteClassConfirmationForm
        @schoolClass={{@controller.schoolClassActingUpon}}
        {{! partial application of submitDeleteClassModal action }}
        @onSubmit={{fn @controller.submitDeleteClassModal pagination}}
        @onClose={{fn @controller.showDeleteClassModal false}}
      />
    </LayoutBlocksModal>
    <LayoutBlocksModal
      @title={{t "manage.manageClasses.classes.bulkActions.deleteClasses.title"}}
      @showModal={{@controller.deleteClassesModalOpen}}
      @close={{fn @controller.showDeleteClassesModal false}}
    >
      <FormDeleteClasses
        @schoolClasses={{selectRecords.selectedRecords}}
        @themeKey={{themeKey @model.subscriptionType}}
        @closeModalAction={{fn @controller.showDeleteClassesModal false}}
        @onSuccess={{@controller.deleteClassesSuccess}}
      />
    </LayoutBlocksModal>
  </DataOperationsTable>
</template>

export default RouteTemplate(ManageClassesPage)
